import React, { useState, useMemo, useEffect } from 'react';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { BodyM } from 'components/Typography/Typography';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import Mastercard from 'assets/img/cards/mastercard.svg';
import Visa from 'assets/img/cards/visa.svg';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentFormStyle';
import { makeStyles } from '@material-ui/core';
import CardInfo from './CardInfo';
import SaveCardToggle from './SaveCardToggle';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICardsDialogProps {
	tokens: IPaymentToken[];
	setTokenData: (token: IPaymentToken) => void;
	setFormData: types.payment.setFormData;
	setSaveCard: (saveCard: boolean) => void;
	formData: types.payment.IFormData;
	hideDialog: () => void;
}

const CardsDialog = (props: ICardsDialogProps) => {
	const { tokens, setTokenData, formData, setFormData, setSaveCard, hideDialog } = props;
	const [selectedToken, setSelectedToken] = useState<IPaymentToken | null>(null);
	const [showErrors, setShowErrors] = useState<boolean>(false);
	const [showCvvInfo, setShowCvvInfo] = useState<boolean>(false);

	const [tempFormData, setTempFormData] = useState<types.payment.ICardData>({
		number: '',
		cvv: '',
		date: '',
		holder: '',
		saveCard: false,
	});

	const [validation, setValidation] = useState<types.payment.IValidation>({
		number: { isValid: true, isPotentiallyValid: true, card: null },
		cvv: { isValid: true, isPotentiallyValid: true },
		date: { isValid: true, isPotentiallyValid: true },
		email: { isValid: true, isPotentiallyValid: true },
	});

	const classes = useStyles();

	const errors = useMemo(() => {
		const isInvalid = (field: keyof types.payment.IValidation) =>
			!validation[field].isPotentiallyValid || (showErrors && !validation[field].isValid);
		return {
			number: isInvalid('number'),
			cvv: isInvalid('cvv'),
			date: isInvalid('date'),
			email: false,
		};
	}, [validation, showErrors]);

	const form = useMemo(
		() => (['number', 'cvv', 'date', 'holder'] as unknown) as Array<keyof types.payment.ICardData>,
		[]
	);

	const hasFilledForm = form.some((key) => {
		return (tempFormData[key] as string).trim().length > 0;
	});

	useEffect(() => {
		if (hasFilledForm) {
			setSelectedToken(null);
		}
	}, [setTempFormData, hasFilledForm]);

	useEffect(() => {
		if (formData.number) {
			const token = tokens.find((token) => token.id === formData.tokenId);
			setSelectedToken(token ? token : null);
		}
	}, [formData]);

	const setCard = () => {
		setShowErrors(true);
		if (Object.values(validation).every(({ isValid }) => isValid)) {
			if (formData.tokenId !== selectedToken?.id || hasFilledForm) {
				if (hasFilledForm) {
					setFormData('type', 'card');
					form.forEach((key) => setFormData(key, tempFormData[key] as string));
				} else if (selectedToken) {
					setFormData('type', 'token');
					setTokenData(selectedToken);
				}
				setSaveCard(!!tempFormData.saveCard);
			}

			hideDialog();
		} else if (selectedToken) {
			setFormData('type', 'token');
			setTokenData(selectedToken);
			hideDialog();
		}
	};

	const isButtonDisabled = !(
		!!selectedToken ||
		(formData.type === 'token' && hasFilledForm) ||
		Object.values(errors).every((error) => !error)
	);

	const toggleSaveCard = () =>
		setTempFormData((tempFormData) => ({ ...tempFormData, saveCard: !tempFormData.saveCard }));

	return (
		<CustomDialog
			isOpen
			hideDialog={props.hideDialog}
			rightSideButtons={[
				{
					text: translate('payments.dialog.useCard'),
					color: 'primary-light',
					onClick: () => {
						setCard();
					},
					disabled: isButtonDisabled,
				},
			]}
			dialogBody={
				<div className={classes.paymentMethodContainer}>
					<BodyM color="primary-light" disableMargin>
						{translate('payment.dialog.select')}
					</BodyM>
					<div className={classes.tokensContainer}>
						{tokens.map((token) => (
							<CustomRadio
								key={token.id}
								checked={selectedToken?.id === token.id}
								onChange={() => {
									setSelectedToken(token);
									setTempFormData({
										number: '',
										cvv: '',
										holder: '',
										date: '',
										saveCard: false,
									});
								}}
								label={
									<span className={classes.tokenCard}>
										<img
											src={token.cardType === 'MASTERCARD' ? Mastercard : Visa}
											className={classes.tokenCardLogo}
											alt="card"
										/>
										<BodyM color="primary-light" disableMargin>
											{`${token.cardNumber.slice(0, 4)} ⸱⸱⸱⸱ ⸱⸱⸱⸱ ${token.cardNumber.slice(-4)}`}
										</BodyM>
									</span>
								}
							/>
						))}
					</div>
					<BodyM color="primary-light" disableMargin>
						{translate('payments.dialog.newCard')}
					</BodyM>
					<div className={classes.cardsContainer}>
						<CardInfo
							formData={tempFormData}
							setFormData={(name: keyof types.payment.ICardData, value: string) => {
								setTempFormData((tempFormData) => ({ ...tempFormData, [name]: value }));
							}}
							errors={errors}
							showCvvInfo={showCvvInfo}
							setShowCvvInfo={setShowCvvInfo}
							validation={validation}
							setValidation={setValidation}
							paymentLimitReached={null}
						/>
						<SaveCardToggle checked={tempFormData.saveCard} toggle={toggleSaveCard} />
					</div>
				</div>
			}
			header={translate('payments.method')}
		/>
	);
};

export default CardsDialog;
